import {
  ApiResListsType,
  ApiResponseType,
  OhsApiRequestName,
  OhsApiRequestOptions,
  RequestType,
} from 'global-services/api/OhsApiModels';
import OhsApiRequest from 'global-services/api/OhsApiRequest';
import { ModuleType } from 'global-services/constants/OhsObject';
import { getCurrentTimezone } from 'global-services/OhsDataParse';
import { OhsAllocationItem } from 'global-components/allocation/OhsAllocatableModels';
import { SafetyPlanAllocListRes } from 'safety-plan/OhsSafetyPlanModels';

import OhsInjuryMgmtRecord, {
  OhsAvailableRecordTask,
  OhsInjuryMgmtPayload,
  OhsInjuryMgmtTemplateListPayload,
  OhsInjuryMgmtTemplateRecord,
} from './models/OhsInjuryMgmtRecord';
import { OhsInjuryMgmtFolderRecord } from './models/OhsInjuryMgmtFolderRecord';
import OhsInjuryMgmtTaskRecord from './models/OhsInjuryMgmtTaskRecord';

export const fetchInjuryMgmt = (_id: string): Promise<any> => {
  return OhsApiRequest(RequestType.Module, ModuleType.Injury, OhsApiRequestName.Fetch, {
    _id,
  });
};

export const getInjuryMgmtRecordList = (
  payload: OhsInjuryMgmtTemplateListPayload
): Promise<ApiResListsType<any[]> | null> => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.SubMasterList,
    payload
  );
};

export const getInjuryMgmtLists = (
  filterInfo: OhsInjuryMgmtPayload
): Promise<ApiResListsType<any[]> | null> => {
  return OhsApiRequest(RequestType.Module, ModuleType.Injury, OhsApiRequestName.List, {
    filter: filterInfo,
  });
};

export const addInjuryMgmt = (payload: any, options?: OhsApiRequestOptions) => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.Injury,
    OhsApiRequestName.Create,
    payload,
    options
  );
};

export const editInjuryMgmt = (payload: any, options?: OhsApiRequestOptions) => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.Injury,
    OhsApiRequestName.Edit,
    payload,
    options
  );
};

export const archiveUnarchiveInjuryMgmtRecord = (
  injuryRecordId: string,
  archived: boolean,
  timeZone: string,
  notes: string,
  methodName: OhsApiRequestName
) => {
  return OhsApiRequest(RequestType.Module, ModuleType.WorkManagement, methodName, {
    forType: ModuleType.Injury,
    _id: injuryRecordId,
    tz: timeZone,
    notes,
  });
};

export const deleteInjuryMgmt = (id: string) => {
  return OhsApiRequest(RequestType.Module, ModuleType.Injury, OhsApiRequestName.Delete, {
    _id: id,
  });
};

export const searchInjuryMgmtLists = (searchInfo: any): Promise<ApiResListsType<any[]> | null> => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.TemplateSearch,
    {
      forType: ModuleType.Injury,
      ...searchInfo,
    }
  );
};

// Templates
export const getInjuryMgmtTemplateList = (
  payload: OhsInjuryMgmtTemplateListPayload
): Promise<ApiResListsType<any[]> | null> => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.TemplateList,
    payload
  );
};

export const getInjuryMgmtTemplateAllocatedList = (
  templateIDs: string[]
): Promise<OhsInjuryMgmtTemplateRecord[] | null> => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.TemplateAllocatedList,
    { _ids: templateIDs, forType: ModuleType.Injury }
  );
};

export const createInjuryMgmtTemplate = (payload: any) => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.TemplateCreate,
    payload
  );
};
export const editInjuryMgmtTemplate = (payload: any) => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.TemplateEdit,
    payload
  );
};

export const archiveUnarchiveInjuryMgmtTemplate = (id: string, archived: boolean) => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    archived ? OhsApiRequestName.TemplateArchive : OhsApiRequestName.TemplateUnArchive,
    {
      _id: id,
      forType: ModuleType.Injury,
    }
  );
};

export const getInjuryMgmtTemplate = async (params: {
  _id?: string;
  disableErrorToast?: boolean;
  master?: boolean;
}): Promise<OhsInjuryMgmtTemplateRecord> => {
  const res: OhsInjuryMgmtTemplateRecord | null = await OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.TemplateFetch,
    {
      ...(params._id && { _id: params._id }),
      forType: ModuleType.Injury,
      ...(params.master && { master: params.master }),
    },
    { disableErrorToast: params.disableErrorToast }
  );
  return res || new OhsInjuryMgmtTemplateRecord();
};
export const fetchInjuryMgmtMasterTemplate = (): Promise<any> => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.ExportTemplateFetch,
    {
      forType: ModuleType.Injury,
      master: true,
    }
  );
};

export const fetchInjuryMgmtTemplate = (_id: string): Promise<any> => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.ExportTemplateFetch,
    {
      forType: ModuleType.Injury,
      _id,
    }
  );
};

export const fetchMasterRecord = (_id: string): Promise<OhsInjuryMgmtFolderRecord | null> => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.MasterFetch,
    {
      forType: ModuleType.Injury,
      _id,
    }
  );
};

export const createMasterRecord = (payload: any) => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.MasterCreate,
    payload
  );
};

export const editMasterRecord = (payload: any) => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.MasterEdit,
    payload
  );
};

export const createInjuryMgmtRecord = (payload: any) => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.SubMasterCreate,
    payload
  );
};

export const getInjuryMgmtRecord = async (
  _id: string,
  options: OhsApiRequestOptions = {}
): Promise<OhsInjuryMgmtRecord> => {
  const res: OhsInjuryMgmtRecord | null = await OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.SubMasterFetch,
    { _id, forType: ModuleType.Injury },
    options
  );
  return res || new OhsInjuryMgmtRecord();
};

export const allocateInjuryMgmt = (
  _id: string,
  allocationItem: OhsAllocationItem | OhsAllocationItem[]
): Promise<any> => {
  const payload: any = {
    _id,
    to: [],
    tzDateCreated: getCurrentTimezone(),
    forType: ModuleType.Injury,
  };
  if (Array.isArray(allocationItem)) {
    payload.to = allocationItem.map((item) => ({
      _id: item._id,
      type: item.type,
    }));
  } else {
    payload.to = allocationItem;
  }

  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.TemplateAllocate,
    payload
  );
};

export const deAllocateInjuryMgmt = (
  _id: string,
  allocationItem: OhsAllocationItem | OhsAllocationItem[]
): Promise<any> => {
  const payload: any = {
    _id,
    to: [],
    tzDateCreated: getCurrentTimezone(),
    forType: ModuleType.Injury,
  };
  if (Array.isArray(allocationItem)) {
    payload.to = allocationItem.map((item) => ({
      _id: item._id,
      type: item.type,
    }));
  } else {
    payload.to = allocationItem;
  }

  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.TemplateDeallocate,
    payload
  );
};

export const getInjuryMgmtAllocationList = (
  injuryMgmtId: string
): Promise<ApiResponseType<SafetyPlanAllocListRes> | null> => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.TemplateAllocationList,
    {
      _id: injuryMgmtId,
      forType: ModuleType.Injury,
    }
  );
};

export const editInjuryMgmtRecord = (payload: any) => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.SubMasterEdit,
    payload
  );
};

export const getInjuryMgmtTaskRecord = async (
  _id: string,
  taskId: string,
  disableErrorToast?: boolean
): Promise<OhsInjuryMgmtTaskRecord> => {
  const res: OhsInjuryMgmtTaskRecord | null = await OhsApiRequest(
    RequestType.Module,
    ModuleType.Task,
    OhsApiRequestName.Fetch,
    {
      _id: taskId,
      for: {
        _id,
        type: ModuleType.InjurySubMaster,
      },
    },
    { disableErrorToast }
  );
  return res || new OhsInjuryMgmtTaskRecord();
};

export const fetchInjuryMgmtTemplatesByIds = async (
  _ids: string[],
  apiRequestName: OhsApiRequestName
): Promise<OhsInjuryMgmtTemplateRecord[] | OhsInjuryMgmtFolderRecord[]> => {
  const res: OhsInjuryMgmtTemplateRecord[] | OhsInjuryMgmtFolderRecord[] | null =
    await OhsApiRequest(RequestType.Module, ModuleType.WorkManagement, apiRequestName, {
      _ids,
      forType: ModuleType.Injury,
    });
  return res || [];
};

export const getInjuryMgmtRecordLinkedActionList = async (_id: string) => {
  const res: any = await OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.LinkedActions,
    {
      _id,
      forType: ModuleType.Injury,
    }
  );
  return res;
};

export const signOffInjuryMgmtRecord = (payload: any, disableErrorToast?: boolean) =>
  OhsApiRequest(RequestType.Module, ModuleType.WorkManagement, OhsApiRequestName.SignOff, payload, {
    disableErrorToast,
  });

export const startInjuryMgmtRecordSignOff = (payload: any) => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.SignOffStart,
    { ...payload, forType: ModuleType.Injury }
  );
};

export const createTaskInjuryMgmtRecord = (payload: any, options: OhsApiRequestOptions = {}) => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.CreateTask,
    payload,
    options
  );
};

export const fetchActiveHistoryRecordTasks = (_id: string, complete: boolean, filter?: any) => {
  return OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.SubMasterTasksList,
    {
      forType: ModuleType.Injury,
      _id,
      complete,
      filter: {
        page: 1,
        count: false,
        sort: {
          order: 1,
          key: 'dateDue',
        },
        ...filter,
      },
    }
  ) as Promise<any[]>;
};

export const getAvailableRecordListTask = async (
  _ids: string | string[]
): Promise<OhsAvailableRecordTask[] | null> => {
  let idList = [];

  if (typeof _ids === 'string') {
    idList = [_ids];
  } else {
    idList = _ids;
  }
  const response: any = await OhsApiRequest(
    RequestType.Module,
    ModuleType.WorkManagement,
    OhsApiRequestName.ListAvailableTask,
    { forType: ModuleType.Injury, _ids: idList },
    { disableErrorToast: true }
  );
  return response;
};
